import { Link } from 'react-router'
import { Button } from '@/components/Button'
import { Card, CardBody, CardHeader, PageHeader } from '@/components/Card'
import { EmptyState } from '@/components/EmptyState'
import { ErrorNotice } from '@/components/ErrorNotice'
import { NotificationList } from '@/components/NotificationList'
import { RefreshButton } from '@/components/RefreshButton'
import { RequestCard } from '@/components/RequestCard'
import { ServiceStatusList } from '@/components/ServiceStatusList'
import { SkeletonRows } from '@/components/Skeleton'
import { StaleNotice } from '@/components/StaleNotice'
import { useAuth } from '@/hooks/useAuth'
import { useMarkNotificationsRead, useNotifications } from '@/hooks/useNotifications'
import { isStaleList, useRequests } from '@/hooks/useRequests'
import type { MediaRequest } from '@/lib/types'

const ACTIVE_STAGES = new Set(['pending', 'approved', 'downloading', 'failed'])

const isInFlight = (request: MediaRequest): boolean => ACTIVE_STAGES.has(request.stage)

/**
 * The tally line above the board: how many things are moving, by stop. Counts
 * are mono so the numbers hold their column as they change, labels are signage
 * so the strip reads as part of the board's signing rather than as stat cards.
 */
function BoardTally({ items }: { items: MediaRequest[] }) {
  const counts = {
    transit: items.filter((item) => item.stage === 'downloading' || item.stage === 'approved')
      .length,
    pending: items.filter((item) => item.stage === 'pending').length,
    ready: items.filter((item) => item.stage === 'available').length,
    failed: items.filter((item) => item.stage === 'failed').length,
  }

  const entries: { label: string; value: number; tone: string }[] = [
    { label: 'In transit', value: counts.transit, tone: 'text-accent' },
    { label: 'Pending', value: counts.pending, tone: 'text-warn' },
    { label: 'Ready', value: counts.ready, tone: 'text-ok' },
  ]
  if (counts.failed > 0) {
    entries.push({ label: 'Failed', value: counts.failed, tone: 'text-danger' })
  }

  return (
    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1.5 rounded border border-line bg-surface px-3 py-2.5">
      {entries.map((entry, index) => (
        <span key={entry.label} className="flex items-baseline gap-1.5">
          {index > 0 ? (
            <span aria-hidden className="mr-0.5 text-faint">
              ·
            </span>
          ) : null}
          <span className={`font-mono text-sm ${entry.value > 0 ? entry.tone : 'text-faint'}`}>
            {entry.value}
          </span>
          <span className="signage text-[9px] text-faint">{entry.label}</span>
        </span>
      ))}
    </div>
  )
}

/**
 * Where to go next: watch something, or ask for something.
 *
 * These are the two things a user actually leaves this portal to do, so they
 * sit directly under the masthead rather than being buried in a menu. Styled as
 * board destination plates — the amber chevron is the only ornament, and it
 * points the way out.
 */
function Destinations({ jellyfinUrl, seerrUrl }: { jellyfinUrl: string; seerrUrl: string | null }) {
  // Detail lines are kept to two words: at 390px anything longer truncates
  // mid-sentence, which reads as broken rather than terse.
  // Guides is internal, so it routes rather than opening a tab.
  const links: { href: string; label: string; detail: string; external: boolean }[] = [
    { href: jellyfinUrl, label: 'Watch', detail: 'Open Jellyfin', external: true },
    ...(seerrUrl
      ? [{ href: seerrUrl, label: 'Request', detail: 'Open Seerr', external: true }]
      : []),
    { href: '/guides', label: 'Guides', detail: 'Set up a device', external: false },
  ]

  return (
    <nav aria-label="Destinations" className="grid grid-cols-2 gap-3 sm:grid-cols-3">
      {links.map((link) => (
        <a
          key={link.href}
          href={link.href}
          {...(link.external ? { target: '_blank', rel: 'noreferrer' } : {})}
          className="group flex min-h-16 items-center justify-between gap-2 rounded border border-line bg-surface px-3.5 py-3 transition-colors hover:border-accent/50 hover:bg-elevated sm:px-4"
        >
          <span className="min-w-0">
            <span className="signage block text-[11px] text-ink">{link.label}</span>
            <span className="mt-1 block truncate text-xs text-faint">{link.detail}</span>
          </span>
          <svg
            viewBox="0 0 24 24"
            className="size-4 shrink-0 text-accent transition-transform group-hover:translate-x-0.5"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M5 12h13m-5-6 6 6-6 6"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </a>
      ))}
    </nav>
  )
}

export function DashboardPage() {
  const { user, server } = useAuth()
  const requests = useRequests('all')
  const notifications = useNotifications({ limit: 6 })
  const markRead = useMarkNotificationsRead()

  const items = requests.data?.items ?? []

  /*
   * Unreleased titles sink to the bottom of the board.
   *
   * They cannot move until the film exists, so they carry no information worth
   * scanning past — but they are still the user's requests, so they stay
   * visible (quieted, in a shorter row) rather than being filtered out. The
   * limit is raised to 8 so a couple of far-future titles cannot push a live
   * download off the list.
   */
  const active = items
    .filter(isInFlight)
    .sort((a, b) => Number(a.awaitingRelease ?? false) - Number(b.awaitingRelease ?? false))
    .slice(0, 8)

  const recentlyAvailable = items.filter((item) => item.stage === 'available').slice(0, 3)
  const notificationItems = notifications.data?.items ?? []
  const hasUnread = notificationItems.some((item) => item.readAt === null)

  return (
    <div className="space-y-5">
      <PageHeader
        title={user ? `${user.username}'s board` : 'Board'}
        description="Everything you have asked for, and how far along it is."
        action={<RefreshButton />}
      />

      {server ? (
        <Destinations jellyfinUrl={server.jellyfinUrl} seerrUrl={server.seerrUrl} />
      ) : null}

      <div className="grid gap-5 lg:grid-cols-3 lg:gap-6">
        {/* The board itself opens the page — no hero number in front of it. */}
        <section className="space-y-3 lg:col-span-2" aria-labelledby="board-heading">
          <BoardTally items={items} />

          {isStaleList(requests.data) ? <StaleNotice /> : null}

          <Card>
            <CardHeader
              title={<span id="board-heading">In transit</span>}
              action={
                <Link
                  to="/requests"
                  className="signage text-[10px] text-accent hover:underline"
                >
                  All requests
                </Link>
              }
            />
            <CardBody flush>
              {requests.isPending ? (
                <div className="p-4">
                  <SkeletonRows rows={3} />
                </div>
              ) : requests.isError && items.length === 0 ? (
                <div className="p-4">
                  <ErrorNotice error={requests.error} onRetry={() => void requests.refetch()} />
                </div>
              ) : active.length === 0 ? (
                <div className="p-4">
                  <EmptyState
                    title="Nothing on the board"
                    description="Ask for a film or series on the request site and it will appear here, stop by stop."
                    action={
                      <Link to="/requests">
                        <Button variant="secondary" size="sm">
                          See past requests
                        </Button>
                      </Link>
                    }
                  />
                </div>
              ) : (
                active.map((request, index) => (
                  <RequestCard key={request.id} request={request} index={index} />
                ))
              )}
            </CardBody>
          </Card>

          {recentlyAvailable.length > 0 ? (
            <Card>
              <CardHeader title="Arrived" />
              <CardBody flush>
                {recentlyAvailable.map((request, index) => (
                  <RequestCard key={request.id} request={request} index={index} />
                ))}
              </CardBody>
            </Card>
          ) : null}
        </section>

        <div className="space-y-5 lg:space-y-6">
          <Card>
            <CardHeader
              title="Notifications"
              action={
                hasUnread ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    loading={markRead.isPending}
                    onClick={() => markRead.mutate(undefined)}
                  >
                    Mark all read
                  </Button>
                ) : null
              }
            />
            <CardBody>
              {notifications.isPending ? (
                <div className="space-y-3">
                  <div className="h-3 w-3/4 animate-shimmer rounded-[3px] bg-line" />
                  <div className="h-3 w-1/2 animate-shimmer rounded-[3px] bg-line" />
                </div>
              ) : notifications.isError && notificationItems.length === 0 ? (
                <ErrorNotice error={notifications.error} compact />
              ) : notificationItems.length === 0 ? (
                <p className="text-sm text-muted">
                  Nothing yet. Hermes tells you here when a request moves along.
                </p>
              ) : (
                <NotificationList items={notificationItems} />
              )}
            </CardBody>
          </Card>

          <ServiceStatusList />
        </div>
      </div>
    </div>
  )
}
